import LZString from 'lz-string';
import { KMDeckConfig, KMDeckShareData, KMDeckButton, KMDeckButtonState, GridSetupData, ButtonEditorData, EncryptedShareData, SensitiveData, ReducedEncryptedShareData, DeduplicatedKMDeckShareData, DeduplicatedButton, ButtonReference } from '../types/KMDeck';
import { decryptShareData, encryptSensitiveData, decryptSensitiveData, generatePasswordHash } from '../utils/encryption';
import { getAllR1C1Positions } from '../utils/r1c1Utils';
import { UrlShorteningService } from './UrlShorteningService';

/**
 * Service for handling KMDeck operations
 */
export class KMDeckService {
  /**
   * Creates a new KMDeck configuration with default values
   */
  static createNewConfig(rows: number, columns: number, remoteTriggerUrl: string = '', isLandscape?: boolean, faviconUrl?: string): KMDeckConfig {
    return {
      rows,
      columns,
      pages: [
        {
          id: 'page-1',
          name: 'Page 1',
          buttons: []
        }
      ],
      timestamp: Date.now(),
      version: '1.0.0',
      remoteTriggerUrl,
      isLandscape,
      faviconUrl,
      theme: {
        pageBackground: '#171923', // gray.900
        buttonEdges: '#333333', // original empty button border
        emptyButtonBackground: '#1a1a1a', // original empty button background
        glowEffect: '#ffa500', // original glow color
        glowDisabled: false,
      }
    };
  }

  /**
   * Creates an empty button for a given R1C1 position
   */
  static createEmptyButton(pageId: string, r1c1Position: string): KMDeckButton {
    return {
      id: `button-${pageId}-${r1c1Position}`,
      title: '',
      backgroundColor: '#1a1a1a',
      backgroundImageUrl: '',
      macroUuid: '',
      parameter: '',
      fontFamily: 'Arial, sans-serif',
      fontSize: 18,
      hideTitle: false
    };
  }

  /**
   * Generates a Keyboard Maestro remote trigger URL
   * This uses Keyboard Maestro's remote trigger service
   */
  static generateKeyboardMaestroUrl(button: KMDeckButton, remoteTriggerUrl: string): string {
    if (!button.macroUuid || !remoteTriggerUrl) {
      return '';
    }
    
    // Use Keyboard Maestro's remote trigger format
    // Format: https://trigger.keyboardmaestro.com/t/<UniqueID1>/<UniqueID2>?TriggerValue
    const triggerValue = button.parameter 
      ? `${button.macroUuid}§${button.parameter}`
      : button.macroUuid;
    
    return `${remoteTriggerUrl}?${triggerValue}`;
  }

  /**
   * Validates a Keyboard Maestro remote trigger URL
   */
  static validateKeyboardMaestroUrl(url: string): boolean {
    // Accept Keyboard Maestro remote trigger URLs
    const pattern = /^https:\/\/trigger\.keyboardmaestro\.com\/t\/[^/]+\/[^/]+$/;
    return pattern.test(url);
  }

  /**
   * Compresses and encodes KMDeck share data for URL sharing
   * Automatically deduplicates identical buttons to reduce size
   */
  static compressShareData(shareData: KMDeckShareData): string {
    // Count original buttons
    const originalButtonCount = shareData.config.pages.reduce((sum, page) => sum + page.buttons.length, 0);
    const originalSize = JSON.stringify(shareData).length;
    
    // Create deduplicated version for smaller file size
    const deduplicatedData = this.createDeduplicatedShareData(shareData);
    const deduplicatedSize = JSON.stringify(deduplicatedData).length;
    const librarySize = deduplicatedData.buttonLibrary.length;
    
    // Log deduplication results
    console.log('[KMDeckService] Button Deduplication Results:', {
      originalButtons: originalButtonCount,
      uniqueButtons: librarySize,
      duplicatesRemoved: originalButtonCount - librarySize,
      originalSize: originalSize,
      deduplicatedSize: deduplicatedSize,
      sizeReduction: `${((1 - deduplicatedSize / originalSize) * 100).toFixed(1)}%`,
      compressionRatio: `${(originalSize / deduplicatedSize).toFixed(2)}x`
    });
    
    return LZString.compressToEncodedURIComponent(JSON.stringify(deduplicatedData));
  }

  /**
   * Decompresses and decodes KMDeck share data from URL
   * Handles both old format (non-deduplicated) and new format (deduplicated) data
   */
  static decompressShareData(compressedData: string): KMDeckShareData | null {
    try {
      const decompressed = LZString.decompressFromEncodedURIComponent(compressedData);
      if (!decompressed) return null;
      
      const data = JSON.parse(decompressed);
      
      // Check if this is deduplicated data (has buttonLibrary property)
      if (data.buttonLibrary && Array.isArray(data.buttonLibrary)) {
        // This is deduplicated data, reconstruct it
        console.log('[KMDeckService] Reconstructing deduplicated data:', {
          buttonLibrarySize: data.buttonLibrary.length,
          totalReferences: data.config.pages.reduce((sum: number, page: { buttonReferences?: ButtonReference[] }) => sum + (page.buttonReferences?.length || 0), 0)
        });
        const reconstructedData = this.reconstructShareData(data as DeduplicatedKMDeckShareData);
        return this.validateShareData(reconstructedData) ? reconstructedData : null;
      } else {
        // This is old format data, validate and return as-is
        console.log('[KMDeckService] Loading old format (non-deduplicated) data');
        return this.validateShareData(data) ? data : null;
      }
    } catch (error) {
      console.error('[KMDeckService] Failed to decompress share data:', error);
      return null;
    }
  }

  /**
   * Creates a shareable URL with the given KMDeck data
   */
  static createShareUrl(shareData: KMDeckShareData): string {
    const compressedData = this.compressShareData(shareData);
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('data', compressedData);
    return currentUrl.toString();
  }

  /**
   * Creates a shareable URL with ABmyMix short-link minting
   */
  static async createShareUrlWithShortening(shareData: KMDeckShareData): Promise<string> {
    const fullUrl = this.createShareUrl(shareData);
    console.log('[KMDeckService] Full URL:', fullUrl);
    
    return await UrlShorteningService.shortenUrl(fullUrl);
  }

  /**
   * Creates a password-protected shareable URL with reduced encryption
   * Only encrypts the sensitive data (remoteTriggerUrl and password hash)
   */
  static async createPasswordProtectedShareUrl(
    shareData: KMDeckShareData, 
    password: string
  ): Promise<string> {
    try {
      // Generate password hash for verification
      const passwordHash = await generatePasswordHash(password);
      
      // Create sensitive data object with only the data that needs encryption
      const sensitiveData: SensitiveData = {
        remoteTriggerUrl: shareData.config.remoteTriggerUrl,
        passwordHash
      };
      
      // Encrypt only the sensitive data
      const encryptedSensitiveData = await encryptSensitiveData(sensitiveData, password);
      
      // Create deduplicated version for smaller file size
      const deduplicatedData = this.createDeduplicatedShareData(shareData);
      
      // Create a reduced share data object without sensitive information
      const reducedShareData = {
        config: {
          ...deduplicatedData.config,
          remoteTriggerUrl: '' // Remove sensitive URL from public data
        },
        buttonLibrary: deduplicatedData.buttonLibrary,
        isPasswordProtected: true,
        encryptedSensitiveData: encryptedSensitiveData.encryptedSensitiveData,
        salt: encryptedSensitiveData.salt,
        iv: encryptedSensitiveData.iv
      };
      
      // Compress the reduced data
      const compressedData = LZString.compressToEncodedURIComponent(JSON.stringify(reducedShareData));
      
      // Create URL with encrypted data
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('encrypted', 'true');
      currentUrl.searchParams.set('data', compressedData);
      
      const fullUrl = currentUrl.toString();
      console.log('[KMDeckService] Password-protected URL (reduced encryption):', fullUrl);
      
      // Try to shorten the URL
      return await UrlShorteningService.shortenUrl(fullUrl);
    } catch (error) {
      console.error('[KMDeckService] Failed to create password-protected URL:', error);
      throw new Error('Failed to create password-protected share URL');
    }
  }

  /**
   * Decrypts and decompresses password-protected share data from URL
   * Handles both old full encryption and new reduced encryption formats
   */
  static async decryptShareDataFromUrl(password: string): Promise<KMDeckShareData | null> {
    try {
      const params = new URLSearchParams(window.location.search);
      const isEncrypted = params.get('encrypted') === 'true';
      const data = params.get('data');
      
      if (!data) return null;
      
      if (isEncrypted) {
        // Handle encrypted data
        const compressedData = LZString.decompressFromEncodedURIComponent(data);
        if (!compressedData) return null;
        
        const parsedData = JSON.parse(compressedData);
        
        // Check if this is the new reduced encryption format
        if (parsedData.encryptedSensitiveData) {
          return await this.decryptReducedShareData(parsedData, password);
        }
        
        // Fall back to old full encryption format
        const encryptedData = parsedData as EncryptedShareData;
        return await decryptShareData(encryptedData, password);
      } else {
        // Handle regular compressed data (backward compatibility)
        return this.decompressShareData(data);
      }
    } catch (error) {
      console.error('[KMDeckService] Failed to decrypt share data from URL:', error);
      return null;
    }
  }

  /**
   * Decrypts reduced encryption format share data
   */
  private static async decryptReducedShareData(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reducedShareData: { config: any; buttonLibrary?: DeduplicatedButton[]; encryptedSensitiveData: string; salt: string; iv: string }, 
    password: string
  ): Promise<KMDeckShareData | null> {
    try {
      // Decrypt the sensitive data
      const encryptedSensitiveData: ReducedEncryptedShareData = {
        encryptedSensitiveData: reducedShareData.encryptedSensitiveData,
        salt: reducedShareData.salt,
        iv: reducedShareData.iv,
        isPasswordProtected: true
      };
      const sensitiveData = await decryptSensitiveData(encryptedSensitiveData, password);
      
      // Verify password hash
      const providedPasswordHash = await generatePasswordHash(password);
      if (sensitiveData.passwordHash !== providedPasswordHash) {
        console.error('[KMDeckService] Password verification failed');
        return null;
      }
      
      // Check if this is deduplicated data (has buttonLibrary property)
      if (reducedShareData.buttonLibrary && Array.isArray(reducedShareData.buttonLibrary)) {
        // This is deduplicated data, reconstruct it first
        const deduplicatedData: DeduplicatedKMDeckShareData = {
          config: {
            ...reducedShareData.config,
            remoteTriggerUrl: sensitiveData.remoteTriggerUrl, // Restore the sensitive URL
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            pages: reducedShareData.config.pages.map((page: any) => ({
              id: page.id,
              name: page.name,
              buttonReferences: page.buttonReferences || [],
              landscapePositions: page.landscapePositions,
              portraitPositions: page.portraitPositions,
              isSplitPortraitLandscape: page.isSplitPortraitLandscape
            }))
          },
          buttonLibrary: reducedShareData.buttonLibrary,
          isPasswordProtected: true
        };
        
        // Reconstruct the full share data from deduplicated format
        const fullShareData = this.reconstructShareData(deduplicatedData);
        return fullShareData;
      } else {
        // This is old format data, reconstruct normally
        const fullShareData: KMDeckShareData = {
          config: {
            ...reducedShareData.config,
            remoteTriggerUrl: sensitiveData.remoteTriggerUrl // Restore the sensitive URL
          },
          isPasswordProtected: true
        };
        
        return fullShareData;
      }
    } catch (error) {
      console.error('[KMDeckService] Failed to decrypt reduced share data:', error);
      return null;
    }
  }

  /**
   * Checks if the current URL contains encrypted data
   */
  static isPasswordProtectedUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.get('encrypted') === 'true';
  }

  /**
   * Extracts share data from URL parameters
   */
  static getShareDataFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('data');
  }

  /**
   * Validates KMDeck share data structure
   */
  static validateShareData(data: unknown): data is KMDeckShareData {
    if (!data || typeof data !== 'object') return false;
    
    const shareData = data as KMDeckShareData;
    
    if (!shareData.config || typeof shareData.config !== 'object') return false;
    
    const config = shareData.config;
    if (typeof config.rows !== 'number' || config.rows < 1 || config.rows > 8) return false;
    if (typeof config.columns !== 'number' || config.columns < 1 || config.columns > 8) return false;
    if (!Array.isArray(config.pages) || config.pages.length < 1 || config.pages.length > 3) return false;
    if (typeof config.timestamp !== 'number') return false;
    if (typeof config.version !== 'string') return false;
    if (typeof config.remoteTriggerUrl !== 'string') return false;
    if (config.faviconUrl !== undefined && typeof config.faviconUrl !== 'string') return false;
    
    // Validate each page
    for (const page of config.pages) {
      if (typeof page.id !== 'string' || !page.id) return false;
      if (typeof page.name !== 'string' || !page.name) return false;
      if (!Array.isArray(page.buttons)) return false;
      
      // Validate each button
      for (const button of page.buttons) {
        if (typeof button.id !== 'string' || !button.id) return false;
        if (typeof button.title !== 'string') return false;
        if (typeof button.backgroundColor !== 'string') return false;
        if (button.backgroundImageUrl !== undefined && typeof button.backgroundImageUrl !== 'string') return false;
        if (typeof button.macroUuid !== 'string') return false;
        if (button.parameter !== undefined && typeof button.parameter !== 'string') return false;
      }
    }
    
    return true;
  }

  /**
   * Generates a unique ID for buttons
   */
  static generateUniqueId(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Compares two buttons to see if they have identical configurations
   * (excluding the id field which is always unique)
   */
  static areButtonsIdentical(button1: KMDeckButton, button2: KMDeckButton): boolean {
    // Compare all properties except id
    const propsToCompare: (keyof KMDeckButton)[] = [
      'title', 'backgroundColor', 'backgroundImageUrl', 'macroUuid', 'parameter',
      'fontFamily', 'fontSize', 'fontColor', 'hideTitle', 'isToggle', 'toggleState'
    ];

    for (const prop of propsToCompare) {
      if (button1[prop] !== button2[prop]) {
        return false;
      }
    }

    // Compare toggle states if they exist
    if (button1.toggleState1 && button2.toggleState1) {
      if (!this.areButtonStatesIdentical(button1.toggleState1, button2.toggleState1)) {
        return false;
      }
    } else if (button1.toggleState1 !== button2.toggleState1) {
      return false;
    }

    if (button1.toggleState2 && button2.toggleState2) {
      if (!this.areButtonStatesIdentical(button1.toggleState2, button2.toggleState2)) {
        return false;
      }
    } else if (button1.toggleState2 !== button2.toggleState2) {
      return false;
    }

    return true;
  }

  /**
   * Compares two button states to see if they are identical
   */
  private static areButtonStatesIdentical(state1: KMDeckButtonState | undefined, state2: KMDeckButtonState | undefined): boolean {
    if (!state1 && !state2) return true;
    if (!state1 || !state2) return false;

    const propsToCompare = [
      'title', 'backgroundColor', 'backgroundImageUrl', 'macroUuid', 'parameter',
      'fontFamily', 'fontSize', 'fontColor', 'hideTitle', 'isToggle'
    ];

    for (const prop of propsToCompare) {
      if ((state1 as unknown as Record<string, unknown>)[prop] !== (state2 as unknown as Record<string, unknown>)[prop]) {
        return false;
      }
    }

    return true;
  }

  /**
   * Creates a deduplicated version of KMDeck share data
   * Identical buttons across all pages are stored once in a button library
   */
  static createDeduplicatedShareData(shareData: KMDeckShareData): DeduplicatedKMDeckShareData {
    const buttonLibrary: DeduplicatedButton[] = [];
    const buttonMap = new Map<string, string>(); // Maps original button ID to library button ID
    const buttonReferences: ButtonReference[] = [];

    // Collect all buttons from all pages
    const allButtons: Array<{ button: KMDeckButton; pageId: string; r1c1Position: string }> = [];
    
    for (const page of shareData.config.pages) {
      for (const button of page.buttons) {
        // Extract R1C1 position from button ID (format: button-pageId-R1C1 or button-pageId-landscape-R1C1)
        const idParts = button.id.split('-');
        const r1c1Position = idParts[idParts.length - 1]; // Last part is R1C1
        allButtons.push({ button, pageId: page.id, r1c1Position });
      }
    }

    // Process each button to find duplicates
    for (const { button, pageId, r1c1Position } of allButtons) {
      // Check if we already have an identical button in the library
      let libraryButtonId = buttonMap.get(button.id);
      
      if (!libraryButtonId) {
        // Look for identical button in library
        const existingButton = buttonLibrary.find(libButton => 
          this.areButtonsIdentical(button, libButton as KMDeckButton)
        );
        
        if (existingButton) {
          libraryButtonId = existingButton.id;
        } else {
          // Create new library entry
          const libraryButton: DeduplicatedButton = {
            id: `lib-${buttonLibrary.length + 1}`,
            title: button.title,
            backgroundColor: button.backgroundColor,
            backgroundImageUrl: button.backgroundImageUrl,
            macroUuid: button.macroUuid,
            parameter: button.parameter,
            fontFamily: button.fontFamily,
            fontSize: button.fontSize,
            fontColor: button.fontColor,
            hideTitle: button.hideTitle,
            isToggle: button.isToggle,
            toggleState: button.toggleState,
            toggleState1: button.toggleState1,
            toggleState2: button.toggleState2
          };
          
          buttonLibrary.push(libraryButton);
          libraryButtonId = libraryButton.id;
        }
        
        buttonMap.set(button.id, libraryButtonId);
      }

      // Add reference
      buttonReferences.push({
        buttonId: libraryButtonId,
        pageId,
        r1c1Position
      });
    }

    // Group references by page
    const pageReferences = new Map<string, ButtonReference[]>();
    for (const ref of buttonReferences) {
      if (!pageReferences.has(ref.pageId)) {
        pageReferences.set(ref.pageId, []);
      }
      pageReferences.get(ref.pageId)!.push(ref);
    }

    // Create deduplicated pages
    const deduplicatedPages = shareData.config.pages.map(page => ({
      id: page.id,
      name: page.name,
      buttonReferences: pageReferences.get(page.id) || [],
      landscapePositions: page.landscapePositions,
      portraitPositions: page.portraitPositions,
      isSplitPortraitLandscape: page.isSplitPortraitLandscape
    }));

    return {
      config: {
        ...shareData.config,
        pages: deduplicatedPages
      },
      buttonLibrary,
      isPasswordProtected: shareData.isPasswordProtected,
      passwordHash: shareData.passwordHash
    };
  }

  /**
   * Reconstructs full KMDeck share data from deduplicated data
   */
  static reconstructShareData(deduplicatedData: DeduplicatedKMDeckShareData): KMDeckShareData {
    const buttonLibraryMap = new Map<string, DeduplicatedButton>();
    for (const button of deduplicatedData.buttonLibrary) {
      buttonLibraryMap.set(button.id, button);
    }

    // Reconstruct pages with full button data
    const reconstructedPages = deduplicatedData.config.pages.map(page => {
      const buttons: KMDeckButton[] = [];
      
      for (const ref of page.buttonReferences) {
        const libraryButton = buttonLibraryMap.get(ref.buttonId);
        if (libraryButton) {
          // Create full button with original ID format
          const buttonId = `button-${page.id}-${ref.r1c1Position}`;
          const fullButton: KMDeckButton = {
            id: buttonId,
            title: libraryButton.title,
            backgroundColor: libraryButton.backgroundColor,
            backgroundImageUrl: libraryButton.backgroundImageUrl,
            macroUuid: libraryButton.macroUuid,
            parameter: libraryButton.parameter,
            fontFamily: libraryButton.fontFamily,
            fontSize: libraryButton.fontSize,
            fontColor: libraryButton.fontColor,
            hideTitle: libraryButton.hideTitle,
            isToggle: libraryButton.isToggle,
            toggleState: libraryButton.toggleState,
            toggleState1: libraryButton.toggleState1,
            toggleState2: libraryButton.toggleState2
          };
          buttons.push(fullButton);
        }
      }

      return {
        id: page.id,
        name: page.name,
        buttons,
        landscapePositions: page.landscapePositions,
        portraitPositions: page.portraitPositions,
        isSplitPortraitLandscape: page.isSplitPortraitLandscape
      };
    });

    return {
      config: {
        ...deduplicatedData.config,
        pages: reconstructedPages
      },
      isPasswordProtected: deduplicatedData.isPasswordProtected,
      passwordHash: deduplicatedData.passwordHash
    };
  }

  /**
   * Validates grid setup data
   */
  static validateGridSetup(data: GridSetupData): boolean {
    return (
      typeof data.rows === 'number' &&
      data.rows >= 1 &&
      data.rows <= 8 &&
      typeof data.columns === 'number' &&
      data.columns >= 1 &&
      data.columns <= 8
    );
  }

  /**
   * Validates button editor data
   */
  static validateButtonEditor(data: ButtonEditorData): boolean {
    return (
      typeof data.title === 'string' &&
      typeof data.backgroundColor === 'string' &&
      typeof data.macroUuid === 'string' &&
      data.macroUuid.length > 0
    );
  }

  /**
   * Creates a new page in the configuration
   */
  static addPage(config: KMDeckConfig): KMDeckConfig {
    console.log('[KMDeckService.addPage] Starting addPage with config:', {
      totalPages: config.pages.length,
      pageIds: config.pages.map(p => p.id)
    });
    
    // Only count main pages (not landscape pages) for the 3-page limit
    const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
    console.log('[KMDeckService.addPage] Main pages:', {
      count: mainPages.length,
      pageIds: mainPages.map(p => p.id)
    });
    
    if (mainPages.length >= 3) {
      console.log('[KMDeckService.addPage] Page limit reached, not adding new page');
      return config;
    }
    
    const newPageNumber = mainPages.length + 1;
    console.log('[KMDeckService.addPage] Creating new page:', {
      newPageNumber,
      newPageId: `page-${newPageNumber}`
    });
    
    const newPage = {
      id: `page-${newPageNumber}`,
      name: `Page ${newPageNumber}`,
      buttons: []
    };
    
    const result = {
      ...config,
      pages: [...config.pages, newPage]
    };
    
    console.log('[KMDeckService.addPage] Result:', {
      totalPages: result.pages.length,
      pageIds: result.pages.map(p => p.id)
    });
    
    return result;
  }

  /**
   * Removes a page from the configuration
   */
  static removePage(config: KMDeckConfig, pageId: string): KMDeckConfig {
    if (config.pages.length <= 1) return config;
    
    return {
      ...config,
      pages: config.pages.filter(page => page.id !== pageId)
    };
  }

  /**
   * Renames a page in the configuration
   */
  static renamePage(config: KMDeckConfig, pageId: string, newName: string): KMDeckConfig {
    return {
      ...config,
      pages: config.pages.map(page => 
        page.id === pageId ? { ...page, name: newName } : page
      )
    };
  }

  /**
   * Adds a row to the configuration
   */
  static addRow(config: KMDeckConfig): KMDeckConfig {
    if (config.rows >= 8) return config;
    
    const newRows = config.rows + 1;
    
    return {
      ...config,
      rows: newRows,
      pages: config.pages.map(page => {
        // Get all R1C1 positions for the new grid
        const allR1C1Positions = getAllR1C1Positions(newRows, config.columns);
        
        // Create a map of existing buttons by their R1C1 position
        const existingButtons = new Map<string, KMDeckButton>();
        page.buttons.forEach(button => {
          const parts = button.id.split('-');
          const buttonR1C1 = parts[parts.length - 1];
          existingButtons.set(buttonR1C1, button);
        });
        
        // Create new buttons array with all positions
        const newButtons: KMDeckButton[] = [];
        for (const r1c1Position of allR1C1Positions) {
          if (existingButtons.has(r1c1Position)) {
            // Keep existing button
            newButtons.push(existingButtons.get(r1c1Position)!);
          } else {
            // Add new empty button
            newButtons.push(this.createEmptyButton(page.id, r1c1Position));
          }
        }
        
        return {
          ...page,
          buttons: newButtons
        };
      })
    };
  }

  /**
   * Removes a row from the configuration
   */
  static removeRow(config: KMDeckConfig): KMDeckConfig {
    if (config.rows <= 1) return config;
    
    const newRows = config.rows - 1;
    
    return {
      ...config,
      rows: newRows,
      pages: config.pages.map(page => {
        // Get all R1C1 positions for the new grid
        const allR1C1Positions = getAllR1C1Positions(newRows, config.columns);
        
        // Create a map of existing buttons by their R1C1 position
        const existingButtons = new Map<string, KMDeckButton>();
        page.buttons.forEach(button => {
          const parts = button.id.split('-');
          const buttonR1C1 = parts[parts.length - 1];
          existingButtons.set(buttonR1C1, button);
        });
        
        // Keep only buttons that exist in the new grid
        const newButtons: KMDeckButton[] = [];
        for (const r1c1Position of allR1C1Positions) {
          if (existingButtons.has(r1c1Position)) {
            newButtons.push(existingButtons.get(r1c1Position)!);
          }
        }
        
        return {
          ...page,
          buttons: newButtons
        };
      })
    };
  }

  /**
   * Adds a column to the configuration
   */
  static addColumn(config: KMDeckConfig): KMDeckConfig {
    if (config.columns >= 8) return config;
    
    const newColumns = config.columns + 1;
    
    return {
      ...config,
      columns: newColumns,
      pages: config.pages.map(page => {
        // Get all R1C1 positions for the new grid
        const allR1C1Positions = getAllR1C1Positions(config.rows, newColumns);
        
        // Create a map of existing buttons by their R1C1 position
        const existingButtons = new Map<string, KMDeckButton>();
        page.buttons.forEach(button => {
          const parts = button.id.split('-');
          const buttonR1C1 = parts[parts.length - 1];
          existingButtons.set(buttonR1C1, button);
        });
        
        // Create new buttons array with all positions
        const newButtons: KMDeckButton[] = [];
        for (const r1c1Position of allR1C1Positions) {
          if (existingButtons.has(r1c1Position)) {
            // Keep existing button
            newButtons.push(existingButtons.get(r1c1Position)!);
          } else {
            // Add new empty button
            newButtons.push(this.createEmptyButton(page.id, r1c1Position));
          }
        }
        
        return {
          ...page,
          buttons: newButtons
        };
      })
    };
  }

  /**
   * Removes a column from the configuration
   */
  static removeColumn(config: KMDeckConfig): KMDeckConfig {
    if (config.columns <= 1) return config;
    
    const newColumns = config.columns - 1;
    
    return {
      ...config,
      columns: newColumns,
      pages: config.pages.map(page => {
        // Get all R1C1 positions for the new grid
        const allR1C1Positions = getAllR1C1Positions(config.rows, newColumns);
        
        // Create a map of existing buttons by their R1C1 position
        const existingButtons = new Map<string, KMDeckButton>();
        page.buttons.forEach(button => {
          const parts = button.id.split('-');
          const buttonR1C1 = parts[parts.length - 1];
          existingButtons.set(buttonR1C1, button);
        });
        
        // Keep only buttons that exist in the new grid
        const newButtons: KMDeckButton[] = [];
        for (const r1c1Position of allR1C1Positions) {
          if (existingButtons.has(r1c1Position)) {
            newButtons.push(existingButtons.get(r1c1Position)!);
          }
        }
        
        return {
          ...page,
          buttons: newButtons
        };
      })
    };
  }

  /**
   * Exports KMDeck configuration to a downloadable JSON file
   */
  static async exportConfig(config: KMDeckConfig, filename?: string): Promise<void> {
    // Start URL generation in parallel (non-blocking)
    const shareUrlPromise = this.createShareUrlWithShortening({
      config,
      isPasswordProtected: false
    }).catch(error => {
      console.warn('[KMDeckService] Failed to generate share URL, continuing without it:', error);
      return '';
    });
    
    // Prepare config data without waiting for URL
    const configData = {
      ...config,
      exportedAt: new Date().toISOString(),
      exportVersion: '1.0.0'
    };

    // Try to use File System Access API for better file dialog
    if ('showSaveFilePicker' in window) {
      try {
        const fileHandle = await (window as Window & { showSaveFilePicker: (options: { suggestedName?: string; types?: Array<{ description: string; accept: Record<string, string[]> }> }) => Promise<FileSystemFileHandle> }).showSaveFilePicker({
          suggestedName: filename || `kmdeck-config-${new Date().toISOString().split('T')[0]}.json`,
          types: [{
            description: 'KMDeck Configuration Files',
            accept: {
              'application/json': ['.json']
            }
          }]
        });
        
        // Wait for URL generation to complete before writing
        const shareUrl = await shareUrlPromise;
        const finalConfigData = {
          ...configData,
          ...(shareUrl && { shareUrl })
        };
        
        const jsonString = JSON.stringify(finalConfigData, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        return;
      } catch (error) {
        // User cancelled the dialog, throw specific error
        if (error instanceof Error && error.name === 'AbortError') {
          throw new Error('USER_CANCELLED');
        }
        // If File System Access API fails, fall back to download method
        console.log('[KMDeckService] File System Access API failed, falling back to download method');
      }
    }
    
    // Fallback: Use download method
    try {
      // Wait for URL generation to complete before creating download
      const shareUrl = await shareUrlPromise;
      const finalConfigData = {
        ...configData,
        ...(shareUrl && { shareUrl })
      };
      
      const jsonString = JSON.stringify(finalConfigData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `kmdeck-config-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[KMDeckService] Failed to export config:', error);
      throw new Error('Failed to export configuration');
    }
  }

  /**
   * Imports KMDeck configuration from a JSON file
   */
  static async importConfig(file: File): Promise<KMDeckConfig> {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      // Validate the imported data
      if (!this.validateImportedConfig(data)) {
        throw new Error('Invalid configuration file format');
      }
      
      // Convert imported data to current format
      const config: KMDeckConfig = {
        rows: data.rows,
        columns: data.columns,
        pages: data.pages,
        timestamp: data.timestamp || Date.now(),
        version: data.version || '1.0.0',
        remoteTriggerUrl: data.remoteTriggerUrl || '',
        isLandscape: data.isLandscape,
        faviconUrl: data.faviconUrl
      };
      
      return config;
    } catch (error) {
      console.error('[KMDeckService] Failed to import config:', error);
      throw new Error('Failed to import configuration file');
    }
  }

  /**
   * Validates imported configuration data
   */
  static validateImportedConfig(data: unknown): data is KMDeckConfig {
    if (!data || typeof data !== 'object') return false;
    
    const configData = data as Record<string, unknown>;
    
    // Check required fields
    if (typeof configData.rows !== 'number' || configData.rows < 1 || configData.rows > 8) return false;
    if (typeof configData.columns !== 'number' || configData.columns < 1 || configData.columns > 8) return false;
    if (!Array.isArray(configData.pages) || configData.pages.length < 1 || configData.pages.length > 3) return false;
    
    // Validate each page
    for (const page of configData.pages) {
      if (typeof page.id !== 'string' || !page.id) return false;
      if (typeof page.name !== 'string' || !page.name) return false;
      if (!Array.isArray(page.buttons)) return false;
      
      // Validate each button
      for (const button of page.buttons) {
        if (typeof button.id !== 'string' || !button.id) return false;
        if (typeof button.title !== 'string') return false;
        if (typeof button.backgroundColor !== 'string') return false;
        if (button.backgroundImageUrl !== undefined && typeof button.backgroundImageUrl !== 'string') return false;
        if (typeof button.macroUuid !== 'string') return false;
        if (button.parameter !== undefined && typeof button.parameter !== 'string') return false;
      }
    }
    
    return true;
  }
}
